<script>
    import { onMount } from "svelte";

    import { apiMethods } from "../../../Services/index.svelte";

    import { DoctorTitleTypes, TitleType } from "../../../Utils/utilEnums.svelte";
    import NotificationModal from "../../../Components/Common/NotificationModal.svelte";
    import { DateInput } from "../../../Components/Common/index.svelte";
    import { MinAccountNameLength } from "../../../Utils/utilConstants.svelte";

    export let personalDetails = {};
    export let updatePersonalDetailsPhase = () => {};

    let isCreatingAccount = false;

    let userExistsModalOpen = false;

    $: isValidFirstName =
        personalDetails.firstName && personalDetails.firstName.length >= MinAccountNameLength;
    $: isValidMiddleName = personalDetails.middleName
        ? personalDetails.middleName.length >= MinAccountNameLength
        : true;
    $: isValidLastName =
        personalDetails.lastName && personalDetails.lastName.length >= MinAccountNameLength;
    $: isValidPreferredFirstName = personalDetails.preferredName
        ? personalDetails.preferredName.length >= MinAccountNameLength
        : true;
    $: isValidPhone =
        personalDetails.phone &&
        String(personalDetails.phone)
            .replaceAll(" ", "")
            .toLocaleLowerCase()
            .match(/^\(?(\d{2})\)?[\s-]?\d{4}[\s-]?\d{4}$/);

    onMount(() => {
        if (personalDetails.birthDate)
            personalDetails.birthDate = personalDetails.birthDate.split("T")[0];
    });

    const updatePersonalDetails = async () => {
        if (!_checkFieldsValidation()) return;

        let isUserExists = false;

        isCreatingAccount = true;

        await apiMethods.getMethod
            .getUserById(personalDetails.createdUserId)
            .then(async (res) => {
                await apiMethods.putMethod
                    .updateUser({
                        ...res,
                        phone: personalDetails.phone,
                    })
                    .then((res) => {
                        if (res.error && res.error == "User exists")
                            isUserExists = true;
                    });
            });

        isCreatingAccount = false;

        if (isUserExists) userExistsModalOpen = true;
        else if (personalDetails.verifiedPhone) updatePersonalDetailsPhase(3);
        else updatePersonalDetailsPhase(2);
    };

    // HELPER FUNCTIONS
    const _checkFieldsValidation = () => {
        _updateInvalidFieldsStyle();

        return (
            personalDetails.firstName &&
            isValidFirstName &&
            isValidMiddleName &&
            personalDetails.lastName &&
            isValidLastName &&
            isValidPreferredFirstName &&
            personalDetails.birthDate &&
            personalDetails.phone &&
            isValidPhone
        );
    };

    const _updateInvalidFieldsStyle = () => {
        if (!personalDetails.firstName || !isValidFirstName)
            _setErrorStyle("firstNameInput");
        else _resetDefaultStyle("firstNameInput");

        if (personalDetails.middleName && !isValidMiddleName)
            _setErrorStyle("middleNameInput");
        else _resetDefaultStyle("middleNameInput");

        if (!personalDetails.lastName || !isValidLastName)
            _setErrorStyle("lastNameInput");
        else _resetDefaultStyle("lastNameInput");

        if (personalDetails.preferredName && !isValidPreferredFirstName)
            _setErrorStyle("preferredFirstNameInput");
        else _resetDefaultStyle("preferredFirstNameInput");

        if (!personalDetails.birthDate) _setErrorStyle("dobInput");
        else _resetDefaultStyle("dobInput");

        if (!personalDetails.phone || !isValidPhone)
            _setErrorStyle("phoneInput");
        else _resetDefaultStyle("phoneInput");
    };

    const _setErrorStyle = (id) => {
        const field = document.getElementById(`${id}`);
        field.style.outline = "red solid 2px";
    };

    const _resetDefaultStyle = (id) => {
        const field = document.getElementById(`${id}`);
        field.style.outline = "none";
    };
</script>

<p class="text-[16px] text-gray-600 w-full">
    Thanks for validating your email address. We need a few personal details to
    get you started.
</p>

<p class="mandatory text-[12px] text-gray-600 w-full">
    indicates a mandatory field
</p>

<form
    on:submit|preventDefault={updatePersonalDetails}
    novalidate
    class="flex flex-col gap-y-4 p-4 w-full"
>
    <div class="flex flex-col gap-y-2">
        <label
            for="title"
            class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
        >
            Title
        </label>

        <select
            name="title"
            class="select select-sm"
            bind:value={personalDetails.title}
        >
            {#each DoctorTitleTypes as title}
                <option value={title.id}>{title.type}</option>
            {/each}
        </select>
    </div>

    <div class="flex flex-col gap-y-2">
        <label
            for="firstName"
            class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
        >
            First Name
        </label>

        <input
            type="text"
            name="firstName"
            class="input input-sm"
            id="firstNameInput"
            autocomplete="off"
            bind:value={personalDetails.firstName}
            on:focus={() => _resetDefaultStyle("firstNameInput")}
        />

        {#if personalDetails.firstName && !isValidFirstName}
            <p class="text-error">
                Invalid First name length (Min. {MinAccountNameLength} characters)
            </p>
        {/if}
    </div>

    <div class="flex flex-col gap-y-2">
        <label
            for="middleName"
            class="leading-7 text-sm text-gray-600 font-bold text-[16px]"
        >
            Middle Name(s)
        </label>

        <input
            type="text"
            name="middleName"
            class="input input-sm"
            id="middleNameInput"
            autocomplete="off"
            bind:value={personalDetails.middleName}
            on:focus={() => _resetDefaultStyle("middleNameInput")}
        />

        {#if personalDetails.middleName && !isValidMiddleName}
            <p class="text-error">
                Invalid Middle name length (Min. {MinAccountNameLength} characters)
            </p>
        {/if}
    </div>

    <div class="flex flex-col gap-y-2">
        <label
            for="lastName"
            class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
        >
            Last Name
        </label>

        <input
            type="text"
            name="lastName"
            class="input input-sm"
            id="lastNameInput"
            autocomplete="off"
            bind:value={personalDetails.lastName}
            on:focus={() => _resetDefaultStyle("lastNameInput")}
        />

        {#if personalDetails.lastName && !isValidLastName}
            <p class="text-error">
                Invalid Last name length (Min. {MinAccountNameLength} characters)
            </p>
        {/if}
    </div>

    <div class="flex flex-col gap-y-2">
        <label
            for="preferredFirstName"
            class="leading-7 text-sm text-gray-600 font-bold text-[16px]"
        >
            Preferred First Name
        </label>

        <input
            type="text"
            name="preferredFirstName"
            class="input input-sm"
            id="preferredFirstNameInput"
            autocomplete="off"
            bind:value={personalDetails.preferredName}
            on:focus={() => _resetDefaultStyle("preferredFirstNameInput")}
        />

        {#if personalDetails.preferredName && !isValidPreferredFirstName}
            <p class="text-error">
                Invalid preferred name length (Min. {MinAccountNameLength} characters)
            </p>
        {/if}
    </div>

    <div class="flex flex-col gap-y-2">
        <label
            for="dob"
            class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
        >
            Date of Birth
        </label>

        <!-- <input
            type="date"
            name="dob"
            class="input input-sm"
            id="dobInput"
            bind:value={personalDetails.birthDate}
            max={new Date().toISOString().split("T")[0]}
            on:focus={() => _resetDefaultStyle("dobInput")}
        /> -->
        <DateInput
            id="dobInput"
            bind:value={personalDetails.birthDate}
            maxDate={new Date().toISOString().split("T")[0]}
            wrapperClass="input input-sm px-0 input-bordered"
            wrapperClassFocus="inputFocus"
        />
    </div>

    <div class="flex flex-col gap-y-2">
        <label
            for="phone"
            class="mandatory leading-7 text-sm text-gray-600 font-bold text-[16px]"
        >
            Mobile phone
        </label>

        <input
            type="text"
            name="phone"
            class="input input-sm"
            id="phoneInput"
            autocomplete="off"
            bind:value={personalDetails.phone}
            on:focus={() => _resetDefaultStyle("phoneInput")}
        />
    </div>

    <div class="flex w-full">
        <button
            class={`btn btn-sm btn-secondary w-full ${
                isCreatingAccount ? "loading" : ""
            }`}
            on:click|preventDefault={updatePersonalDetails}
            disabled={isCreatingAccount}
        >
            {isCreatingAccount ? "Submitting" : "Next"}
        </button>
    </div>
</form>

<NotificationModal
    isModalOpen={userExistsModalOpen}
    title="User Exists"
    showConfirmBtn={false}
    cancelText="Close"
    preventESCClose
    closeModal={() => (userExistsModalOpen = false)}
>
    Current mobile is used already, please try with different mobile number.
</NotificationModal>

<style>
    .mandatory::before {
        content: " * ";
    }
</style>
